import { useState, useMemo, useEffect } from 'react';
import type { Category } from '../types/category';
import type { CategoryType } from '../types/category';
import '../../styles/category-settings.css';

const CATEGORY_SETTINGS_STORAGE_KEY = 'category_settings';
const DEFAULT_TYPE: CategoryType = 'Discretionary';

interface CategorySettingsProps {
  categories: Category[];
  onClose?: () => void;
}

interface CategoryRow {
  id: number;
  name: string;
  groupId: number | null;
  groupName: string | null;
  isGroup: boolean;
  level: number; // 0 for group, 1 for category
}

export function CategorySettings({ categories }: CategorySettingsProps) {
  const [settings, setSettings] = useState<Record<number, CategoryType>>(() => {
    const stored = localStorage.getItem(CATEGORY_SETTINGS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  });
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState<{ top: number; right: number } | null>(null);

  // Close help popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-help-popup]') && !target.closest('[data-help-content]')) {
        setShowHelpPopup(false);
        setPopupPosition(null);
      }
    };
    if (showHelpPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showHelpPopup]);

  const handleResetToDefaults = () => {
    setSettings({});
    localStorage.removeItem(CATEGORY_SETTINGS_STORAGE_KEY);
    // Trigger a custom event to notify that settings changed
    window.dispatchEvent(new Event('categorySettingsChanged'));
  };

  // Build category rows grouped by category group
  const categoryRows = useMemo(() => {
    const rows: CategoryRow[] = [];
    
    // Create a map of group IDs to group categories
    const groupMap = new Map<number, Category>();
    const categoryMap = new Map<number, Category>();
    
    categories.forEach(cat => {
      if (cat.is_group) {
        groupMap.set(cat.id, cat);
      } else {
        categoryMap.set(cat.id, cat);
      }
    });

    // Get all groups, sorted alphabetically
    const groups = Array.from(groupMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );

    // For each group, add the group row and its categories
    groups.forEach(group => {
      // Add group row
      rows.push({
        id: group.id,
        name: group.name,
        groupId: null,
        groupName: null,
        isGroup: true,
        level: 0,
      });

      // Get categories in this group, sorted alphabetically
      const groupCategories = Array.from(categoryMap.values())
        .filter(cat => cat.group_id === group.id)
        .sort((a, b) => a.name.localeCompare(b.name));

      // Add category rows
      groupCategories.forEach(cat => {
        rows.push({
          id: cat.id,
          name: cat.name,
          groupId: group.id,
          groupName: group.name,
          isGroup: false,
          level: 1,
        });
      });
    });

    // Add categories without a group, sorted alphabetically
    const ungroupedCategories = Array.from(categoryMap.values())
      .filter(cat => !cat.group_id)
      .sort((a, b) => a.name.localeCompare(b.name));

    ungroupedCategories.forEach(cat => {
      rows.push({
        id: cat.id,
        name: cat.name,
        groupId: null,
        groupName: null,
        isGroup: false,
        level: 0,
      });
    });

    return rows;
  }, [categories]);

  // Get categories in a group
  const getGroupCategories = (groupId: number): CategoryRow[] => {
    return categoryRows.filter(row => row.groupId === groupId);
  };

  // Handle radio button change
  const handleTypeChange = (categoryId: number, type: CategoryType, isGroup: boolean) => {
    const newSettings = { ...settings };

    if (isGroup) {
      // Apply to all nested categories
      const groupCategories = getGroupCategories(categoryId);
      groupCategories.forEach(catRow => {
        // Store null explicitly for Ignore, or the type value
        newSettings[catRow.id] = type;
      });
      // Don't store the group setting itself - it's just a mechanism
      // Remove group setting if it exists
      delete newSettings[categoryId];
    } else {
      // Set individual category - store null explicitly for Ignore
      newSettings[categoryId] = type;
    }

    setSettings(newSettings);
    localStorage.setItem(CATEGORY_SETTINGS_STORAGE_KEY, JSON.stringify(newSettings));
    
    // Trigger a custom event to notify App.tsx that settings changed
    window.dispatchEvent(new CustomEvent('categorySettingsChanged'));
  };

  // Get current type for a category (or default)
  const getCategoryType = (categoryId: number): CategoryType => {
    // If category ID exists in settings (even if null), return that value
    // Otherwise return default
    if (categoryId in settings) {
      return settings[categoryId];
    }
    return DEFAULT_TYPE;
  };

  // Get effective type for a group (check if all nested categories have the same type)
  const getGroupType = (groupId: number): CategoryType | null => {
    const groupCategories = getGroupCategories(groupId);
    if (groupCategories.length === 0) return null;

    const types = groupCategories.map(cat => getCategoryType(cat.id));
    const firstType = types[0];
    
    // If all categories have the same type, return it; otherwise return null
    return types.every(type => type === firstType) ? firstType : null;
  };

  const typeOptions: CategoryType[] = ['Income', 'Debt', 'Non-Discretionary', 'Discretionary', null];

  return (
    <div className="category-settings-container" style={{ backgroundColor: '#f9f9f9', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e1e4e8' }}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Categories</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleResetToDefaults}
            style={{
              padding: '6px 10px',
              backgroundColor: '#f6f8fa',
              color: '#586069',
              border: '1px solid #d1d5da',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 700,
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e1e4e8';
              e.currentTarget.style.borderColor = '#c6cbd1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f6f8fa';
              e.currentTarget.style.borderColor = '#d1d5da';
            }}
            title="Reset all categories to default type"
          >
            Reset to Defaults
          </button>
          <div style={{ position: 'relative' }} data-help-popup>
            <span 
              onClick={(e) => {
                e.stopPropagation();
                if (showHelpPopup) {
                  setShowHelpPopup(false);
                  setPopupPosition(null);
                } else {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setPopupPosition({ top: rect.bottom + 5, right: window.innerWidth - rect.right });
                  setShowHelpPopup(true);
                }
              }}
              style={{ 
                cursor: 'pointer',
                fontSize: '16px',
                color: '#666',
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                fontWeight: 600,
                flexShrink: 0,
                userSelect: 'none'
              }}
            >
              ?
            </span>
          </div>
          {showHelpPopup && popupPosition && (
            <div 
              data-help-content
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: `${popupPosition.top}px`,
                right: `${popupPosition.right}px`,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '6px',
                padding: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 10000,
                width: '300px',
                fontSize: '14px',
                lineHeight: '1.5',
                color: '#24292e'
              }}
            >
              Categorize your transactions to control how they're used in cash flow projections. Income categories add to your balance, Debt categories represent loan payments, Non-Discretionary covers bills and necessities, and Discretionary is optional spending. Ignored categories are excluded from projections. Category groups can be overridden by individual category settings. Use the Reset to Defaults button to clear all custom category assignments.
            </div>
          )}
        </div>
      </div>
      
      <div className="category-settings-table-wrapper">
          <table className="category-settings-table">
            <thead>
              <tr>
                <th>Category</th>
                <th title="Income categories represent money coming in">Income</th>
                <th title="Debt categories represent debt payments (loans, credit cards, etc.)">Debt</th>
                <th title="Non-Discretionary categories are bills, necessities, and non-negotiable costs">Non-Discretionary</th>
                <th title="Discretionary categories are optional spending or negotiable amounts (entertainment, dining out, etc.)">Discretionary</th>
                <th title="Ignored categories are excluded from cash flow projections. Ignored category groups have been overriden by individual category settings">Ignore</th>
              </tr>
            </thead>
            <tbody>
              {categoryRows.map((row) => {
                const currentType = row.isGroup 
                  ? getGroupType(row.id)
                  : getCategoryType(row.id);
                
                return (
                  <tr 
                    key={`${row.isGroup ? 'group' : 'cat'}-${row.id}`}
                    className={row.isGroup ? 'group-row' : 'category-row'}
                    style={{ paddingLeft: `${row.level * 20}px` }}
                  >
                    <td className="category-name-cell" style={{ paddingLeft: `${row.level * 20}px` }}>
                      {row.name}
                    </td>
                    {typeOptions.map((type) => {
                      const isChecked = currentType === type;
                      // Use a single radio group name per row so only one can be selected
                      const name = row.isGroup ? `group-${row.id}` : `cat-${row.id}`;
                      
                      return (
                        <td key={type || 'ignore'} className="radio-cell">
                          <input
                            type="radio"
                            name={name}
                            checked={isChecked}
                            onChange={() => handleTypeChange(row.id, type, row.isGroup)}
                            className="category-radio"
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
    </div>
  );
}
